import { useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import { useDispatch, useSelector } from 'react-redux';
import { Table, TableBody, Typography, TableContainer, TableHead, TableRow, Paper, IconButton, Pagination, Stack, Box, Tooltip, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button } from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmailIcon from '@mui/icons-material/Email';
import { toast } from 'react-toastify';
import { deleteUserRequest, getRequestUserList, resetUserDelete, sendResetEmailRequest, clearSendResetEmail } from '../../../redux/modules/staff/slice';
import { DeleteConfirmationModal } from '../../../utils/modal';
import SearchInput from '../../../utils/searchInput';


const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));
const EllipsisText = ({ children }) => (
  <Typography>
    {children}
  </Typography>
);
const tableHeading = ["Name", "Role","Email", "Action"];

let deleteItemId;
let limit = 10;
let pageCount = 0;

// Role labels are derived from the dynamic role list (state.permissions.roles).
// Fall back to a humanized version of the raw key for portal/legacy roles that
// aren't in that list, so the cell is never blank.
const humanizeRole = (role) => {
  if (!role) return '';
  const special = { admin: 'Admin', config: 'Config', clericalStaff: 'Clerical Staff', externalReferrer: 'External User', patient: 'Patient' };
  if (special[role]) return special[role];
  return role.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase()).trim();
};

export default function UsersList({ setItemData, toggleList,setCurrentPage,currentPage }) {
  const dispatch = useDispatch();
  const { getUserListRes, deleteUserRes, sendResetEmailRes, loading } = useSelector((state) => state.staff);
  let currentBranch = useSelector(state => state.branch.currentBranch);
  const { roles } = useSelector((state) => state.permissions);
  const roleDisplay = useMemo(() => {
    const map = {};
    (Array.isArray(roles) ? roles : []).forEach((r) => { map[r.role] = r.displayText; });
    return map;
  }, [roles]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [resetEmailModal, setResetEmailModal] = useState({ open: false, userId: null, userName: '' });

  useEffect(() => {
    if (!currentBranch) { return }
    dispatch(getRequestUserList({ page:currentPage, limit, searchQuery }))
  }, [dispatch,currentBranch, currentPage, searchQuery]);

  useEffect(() => {
    if (currentBranch?.id) {
      dispatch(getRequestUserList({ page:currentPage, limit, searchQuery }))
    }
  }, [dispatch, currentBranch?.id,currentBranch, currentPage, searchQuery]);

  useEffect(() => {
    if (!deleteUserRes) { return }
    if (deleteUserRes?.success) {
      dispatch(getRequestUserList({ page:currentPage, limit, searchQuery }))
    }
    toast(deleteUserRes?.message);
    dispatch(resetUserDelete());
  }, [dispatch, deleteUserRes,searchQuery, currentPage]);

  // Handle send reset email response
  useEffect(() => {
    if (!sendResetEmailRes) return;
    if (sendResetEmailRes?.success) {
      toast.success('Password reset email sent successfully');
    } else {
      toast.error(sendResetEmailRes?.message || 'Failed to send reset email');
    }
    dispatch(clearSendResetEmail());
    setResetEmailModal({ open: false, userId: null, userName: '' });
  }, [sendResetEmailRes, dispatch]);

  const handleSendResetEmail = (user) => {
    setResetEmailModal({
      open: true,
      userId: user._id,
      userName: user.name
    });
  };

  const confirmSendResetEmail = () => {
    dispatch(sendResetEmailRequest(resetEmailModal.userId));
  };

  if (getUserListRes?.dataCount && getUserListRes?.dataCount > limit) {
    pageCount = getUserListRes?.dataCount / limit;
    if (pageCount % 2 !== 0) {
        pageCount = Math.ceil(pageCount)
    }
}
  return (
    <>
     <Stack mb={3} direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="space-between">
        <SearchInput setSearchQuery={(searchValue) => { setSearchQuery(searchValue); setCurrentPage(1); }} />
      </Stack>
      <Box sx={{ overflowX: { xs: 'auto', md: 'visible' }, mx: { xs: -2, sm: 0 }, px: { xs: 2, sm: 0 } }}>
      <TableContainer component={Paper}  className='tableShadow'>
        <Table sx={{ minWidth: { xs: 500, md: 700 } }} aria-label="customized table" className='commonTable'>
          <TableHead>
            <TableRow>
              {tableHeading.map(elem => (<StyledTableCell key={elem}>{elem}</StyledTableCell>))}
            </TableRow>
          </TableHead>
          <TableBody>
            {getUserListRes?.data?.length > 0 ?
              getUserListRes?.data?.map(elem => (
                <StyledTableRow key={elem._id}>
                  <StyledTableCell align="left"><EllipsisText>{elem.name}</EllipsisText></StyledTableCell>
                  <StyledTableCell align="left"><EllipsisText>{roleDisplay[elem.role] || humanizeRole(elem.role)}</EllipsisText></StyledTableCell>
                  <StyledTableCell align="left"><EllipsisText>{elem.email}</EllipsisText></StyledTableCell>
                  <StyledTableCell align="left">
                    <EllipsisText>
                      <Tooltip title="Edit User">
                        <IconButton color="primary" onClick={() => { setItemData(elem); toggleList() }}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Send Password Reset Email">
                        <IconButton color="info" onClick={() => handleSendResetEmail(elem)} disabled={loading}>
                          <EmailIcon />
                        </IconButton>
                      </Tooltip>
                      {getUserListRes?.data?.length > 1 &&
                        <Tooltip title="Delete User">
                          <IconButton color="error" onClick={() => { deleteItemId = elem._id; setIsModalOpen(true) }}>
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      }
                    </EllipsisText>
                  </StyledTableCell>
                </StyledTableRow>
              ))
              :
              <StyledTableRow>
                <StyledTableCell colSpan={tableHeading.length} align="left"><Typography>There is no data to show</Typography></StyledTableCell>
              </StyledTableRow>
            }
          </TableBody>
        </Table>
      </TableContainer>
      </Box>
      {(getUserListRes?.dataCount > limit) && <Pagination count={pageCount} page={currentPage} onChange={(event, value) => { setCurrentPage(value)}} />}
      <DeleteConfirmationModal open={isModalOpen} onClose={() => setIsModalOpen(false)} onConfirm={() => { dispatch(deleteUserRequest(deleteItemId));  setIsModalOpen(false) }} />

      {/* Reset Email Confirmation Dialog */}
      <Dialog open={resetEmailModal.open} onClose={() => setResetEmailModal({ open: false, userId: null, userName: '' })}>
        <DialogTitle>Send Password Reset Email</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to send a password reset email to <strong>{resetEmailModal.userName}</strong>?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetEmailModal({ open: false, userId: null, userName: '' })}>
            Cancel
          </Button>
          <Button onClick={confirmSendResetEmail} variant="contained" disabled={loading}>
            {loading ? 'Sending...' : 'Send Email'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}